import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withStyles } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Launch from '@material-ui/icons/Launch';
import Icon from '@material-ui/core/Icon';

import ORSpaceUsage from 'components/ORSpaceUsage/ORSpaceUsageContainer';
import { bytesToGB } from 'services/conversions';
import ORTypography from 'components/ORTypography/ORTypography';
import FeaturesList from 'components/MissionCtrl/InstancesPage/FeaturesList/FeaturesList';

const propTypes = {
  id: PropTypes.string.isRequired,
  service: PropTypes.string.isRequired,
  // serviceSubtype: PropTypes.string.isRequired,
  shortId: PropTypes.string.isRequired,
  region: PropTypes.string.isRequired,
  dtCreated: PropTypes.string.isRequired,
  dtModified: PropTypes.string.isRequired,
  statusDisplay: PropTypes.string.isRequired,
  version: PropTypes.string.isRequired,
  features: PropTypes.shape({
    storageEngine: PropTypes.string,
  }).isRequired,
  provider: PropTypes.string.isRequired,
  hosts: PropTypes.arrayOf(PropTypes.object).isRequired,
  classes: PropTypes.shape({}).isRequired,
  spaceUsage: PropTypes.shape({
    nodes: PropTypes.arrayOf(PropTypes.shape({ spaceTotal: PropTypes.number })),
  }),
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  getRedisMemoryCapacity: PropTypes.func.isRequired,
};

const defaultProps = {
  spaceUsage: {},
};

export function UnstyledDetailOverview({
  service,
  // serviceSubtype,
  shortId,
  region,
  statusDisplay,
  version,
  id,
  dtCreated,
  dtModified,
  features,
  provider,
  hosts,
  classes,
  spaceUsage,
  userRoles,
  getRedisMemoryCapacity,
}) {
  let statusTextColor;
  if (statusDisplay === 'Ready') {
    statusTextColor = classes.textReady;
  } else if (statusDisplay === 'Failed') {
    statusTextColor = classes.textFailed;
  } else {
    statusTextColor = classes.textBuilding;
  }
  return (
    <React.Fragment>
      <div className={classes.details}>
        <div className={classes.charContainer}>
          <ORSpaceUsage
            userRoles={userRoles}
            instanceId={id}
            instanceIsReady={statusDisplay.toLowerCase() === 'ready'}
          />
        </div>
        <div className={classes.detailsListContainer}>
          <div className={classes.detailsListInfoContainer}>
            <Icon className={classes.detailsListIcon}>language_24</Icon>
            <div className={classes.detailsListLocationContainer}>
              <ORTypography className={classes.detailsListLocation}>{region}</ORTypography>
              <ORTypography className={classes.detailsListSize}>
                {spaceUsage &&
                  service !== 'Redis' &&
                  spaceUsage.nodes &&
                  spaceUsage.nodes.length > 0 &&
                  bytesToGB(spaceUsage.nodes[0].spaceTotal).toFixed(0)}
                {service === 'Redis' && statusDisplay === 'Ready' && getRedisMemoryCapacity(id)}
                GB
                {service !== 'Redis' ? '\u00a0' : '\u00a0RAM\u00a0'}
                <span className={classes.detailsListSizeParenthesis}>&#40;per data node&#41;</span>
              </ORTypography>
            </div>
          </div>
          <ul className={classes.detailsList}>
            <li>
              <Typography variant="subtitle1">
                Engine:&nbsp;
                {service}
              </Typography>
            </li>
            <li>
              <Typography variant="subtitle1" className={statusTextColor}>
                <span className={classes.detailsListReadyState}>State:&nbsp;</span>
                {statusDisplay === 'Ready' ? 'Active' : statusDisplay}
              </Typography>
            </li>
            <li>
              <Typography variant="subtitle1">
                Version:&nbsp;
                {version}
              </Typography>
            </li>
            <li>
              <Typography variant="subtitle1">
                ID:&nbsp;
                {id}
              </Typography>
            </li>
            {service === 'MongoDB' ? (
              <li>
                <Typography variant="subtitle1">
                  Short ID:&nbsp;
                  {shortId}
                </Typography>
              </li>
            ) : null}
            <li>
              <Typography variant="subtitle1">
                Created:&nbsp;
                {moment(dtCreated).format('dddd, MMMM Do YYYY, h:mm:ss a')}
              </Typography>
            </li>
            <li>
              <Typography variant="subtitle1">
                Modified:&nbsp;
                {moment(dtModified).format('dddd, MMMM Do YYYY, h:mm:ss a')}
              </Typography>
            </li>
            {service === 'MongoDB' && (
              <li>
                <Typography variant="subtitle1">
                  Storage Engine: {features.storageEngine}
                </Typography>
              </li>
            )}
            <li>
              <Typography variant="subtitle1">
                Cloud Provider:&nbsp;
                {provider}
              </Typography>
            </li>
            <FeaturesList features={features} />
          </ul>
        </div>
        <div className={classes.featureButtonContainer}>
          {hosts.map((host, index) =>
            (service === 'Elasticsearch' &&
              host.connectionUri &&
              host.role !== 'elasticsearch' &&
              statusDisplay === 'Ready') ||
            host.role === 'cockroachdb_admin' ? (
              <a
                key={index.toString()}
                className={classes.detailLink}
                href={host.connectionUri}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  variant="contained"
                  hover-override={host.role}
                  className={classes[`${host.role}Button`]}
                >
                  <div className={classes.spacer} />
                  {host.role.split('_').join(' ')}
                  <Launch className={classes.featureButtonIcon} />
                </Button>
              </a>
            ) : null,
          )}
        </div>
      </div>
    </React.Fragment>
  );
}

UnstyledDetailOverview.propTypes = propTypes;
UnstyledDetailOverview.defaultProps = defaultProps;

const styles = (theme) => ({
  details: {
    backgroundColor: theme.customColors.white,
    border: `1px solid ${theme.customColors.lightGrey}`,
    justifyContent: 'space-between',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: '0px 16px',
    gridTemplateAreas: `
      'chart list'
      'chart buttons'
    `,
    padding: theme.spacing(1),

    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '2fr 1fr',
    },

    [theme.breakpoints.down('sm')]: {
      gridTemplateAreas: `
      'list'
      'buttons'
      'chart'
    `,
      gridTemplateColumns: '1fr',
    },

    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column-reverse',
      alignItems: 'center',
      '& ul': {
        margin: 0,
        paddingLeft: theme.spacing(2.5),
      },
    },
  },
  charContainer: {
    gridArea: 'chart',
  },
  detailsListContainer: {
    gridArea: 'list',
    justifySelf: 'center',
    [theme.breakpoints.down('md')]: {
      justifySelf: 'flex-start',
    },
  },
  detailsListInfoContainer: {
    display: 'flex',
    flexFlow: 'row wrap',
  },
  detailsListLocationContainer: {
    display: 'flex',
    flexFlow: 'column nowrap',
  },
  detailsList: {
    gridArea: 'list',
    justifySelf: 'center',
    '& li': {
      paddingLeft: theme.spacing(2),
      '& h6': {
        fontFamily: '"Libre Franklin Regular", sans-serif',
        fontSize: '14px',
        lineHeight: '21px',
      },
    },
  },
  detailsListIcon: {
    gridArea: 'list',
    justifySelf: 'flex-start',
    marginLeft: theme.spacing(2),
    height: theme.spacing(3),
    width: theme.spacing(3),
    color: theme.customColors.mediumGrey,
  },
  detailsListLocation: {
    fontFamily: '"Libre Franklin Regular", sans-serif',
    paddingLeft: theme.spacing(2),
  },
  detailsListSize: {
    fontFamily: '"Libre Franklin Regular", sans-serif',
    paddingLeft: theme.spacing(2),
  },
  detailsListSizeParenthesis: {
    fontFamily: '"Libre Franklin Regular", sans-serif',
    color: theme.customColors.mediumGrey,
  },
  detailsListReadyState: {
    color: theme.customColors.black,
  },
  summary: {
    width: '100%',
    display: 'flex',
    flexFlow: 'row wrap',
    maxHeight: '85px',

    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
    [theme.breakpoints.down('md')]: {
      padding: `${theme.spacing(1)}px 0 ${theme.spacing(1)}px 0`,
      maxHeight: 'unset',
    },

    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(1),
      flexFlow: 'column nowrap',
    },
  },
  flexContainer: {
    display: 'flex',
    flexFlow: 'row wrap',
    flex: '1 0 50%',

    [theme.breakpoints.down('md')]: {
      flexFlow: 'column nowrap',
    },
  },
  detailContainer: {
    display: 'flex',
    flexFlow: 'column wrap',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gridArea: 'buttons',

    [theme.breakpoints.down('xs')]: {
      width: '100%',
      marginBottom: theme.spacing(1.25),
    },
  },
  textReady: {
    color: theme.customColors.teal,
  },
  textFailed: {
    color: theme.customColors.magenta,
  },
  textBuilding: {
    color: theme.customColors.burntYellow,
  },
  featureButtonContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridGap: '0px 16px',
    gridTemplateAreas: `
      'kibana'
      'cerebro'
    `,
    gridArea: 'buttons',
    width: '100%',

    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr 1fr',
      gridTemplateAreas: `
        'kibana cerebro'
      `,
      marginBottom: theme.spacing(1.25),
    },

    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
      gridTemplateAreas: `
        'kibana'
        'cerebro'
      `,
    },
  },
  featureButtonIcon: {},
  detailLink: {
    display: 'flex',
    flex: '1 0 50%',
    textDecoration: 'none',
    width: '100%',
  },
  kibanaButton: {
    gridArea: 'kibana',
    marginTop: theme.spacing(1),
    color: theme.customColors.black,
    backgroundColor: theme.customColors.peach,
    width: '100%',
    padding: theme.spacing(1),
    justifyContent: 'space-between',
    fontWeight: 400,
  },
  cerebroButton: {
    gridArea: 'cerebro',
    marginTop: theme.spacing(1),
    color: theme.customColors.black,
    backgroundColor: '#7cbbbe',
    width: '100%',
    padding: theme.spacing(1),
    justifyContent: 'space-between',
    fontWeight: 400,
  },
  cockroachdb_adminButton: {
    marginTop: theme.spacing(1),
    color: theme.customColors.black,
    backgroundColor: '#7cbbbe',
    width: '100%',
    padding: theme.spacing(1),
    justifyContent: 'space-between',
    fontWeight: 400,
  },
  spacer: {
    width: '25px',
  },
});

export default withStyles(styles)(UnstyledDetailOverview);
